import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { Heart, Loader2, ArrowLeft, UserPlus, Mail, Lock, Users, Briefcase, KeyRound, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Only allow same-site internal paths as redirect targets to prevent open-redirect attacks.
const safeNext = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  return raw;
};

const PSLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  // Forgot Password state — mirrors the AuthPage pattern.
  // step: 'request' → ask for email | 'reset' → enter new password | 'success'
  const [showForgot, setShowForgot] = useState(false);
  const [forgotStep, setForgotStep] = useState('request');
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // If the user landed via the email reset link (?resetToken=...), jump straight
  // into the "set new password" step.
  useEffect(() => {
    const token = searchParams.get('resetToken');
    if (token) {
      setResetToken(token);
      setShowForgot(true);
      setForgotStep('reset');
    }
  }, [searchParams]);

  const nextPath = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return safeNext(params.get('next'));
  }, [location.search]);
  const nextQuery = nextPath ? `?next=${encodeURIComponent(nextPath)}` : '';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/ps/login`, {
        email: formData.email.trim().toLowerCase(),
        password: formData.password
      });
      localStorage.setItem('psToken', data.token);
      localStorage.setItem('psUserId', data.user.id);
      localStorage.setItem('psUserName', data.user.candidates?.[0]?.name || '');
      toast.success('Login successful!');
      navigate(nextPath || '/parichay-sangam/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password: send reset link
  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: forgotEmail.trim().toLowerCase(),
          userType: 'ps_user',
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('If the email is registered, a reset link has been sent.');
        // For development convenience the backend may echo the resetToken; auto-advance.
        if (data.resetToken) {
          setResetToken(data.resetToken);
          setForgotStep('reset');
        } else {
          setForgotStep('success');
        }
      } else {
        toast.error(data.detail || 'Failed to send reset instructions');
      }
    } catch {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Reset Password: submit new password with token
  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, newPassword }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setForgotStep('success');
        toast.success('Password reset successful! Please log in with your new password.');
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const backToLogin = () => {
    setShowForgot(false);
    setForgotStep('request');
    setForgotEmail('');
    setResetToken('');
    setNewPassword('');
    setConfirmPassword('');
    // Strip the resetToken query param so the modal doesn't re-open on next render.
    if (searchParams.get('resetToken')) {
      navigate('/parichay-sangam/login', { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-amber-50 py-12 px-4">
      <Card className="w-full max-w-md p-8 shadow-lg border-0">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_eb6102e5-96eb-40a4-9fc1-e76d00d027fa/artifacts/osywbjv5_Parichay%20Sangam%202026%20logo.jpeg"
            alt="Parichay Sangam 2026"
            className="w-24 h-24 mx-auto mb-4 rounded-xl shadow-md object-cover"
          />
          <h1 className="text-2xl font-bold text-gray-900">Parichay Sangam 2026</h1>
          <p className="text-gray-500 text-sm mt-1">
            {showForgot
              ? (forgotStep === 'reset' ? 'Set a new password' : forgotStep === 'success' ? 'Password updated' : 'Reset your password')
              : 'Login to manage your matrimonial profile'}
          </p>
          <p className="text-gray-400 text-xs mt-1">परिचय संगम 2026</p>
        </div>

        {!showForgot && (
          <>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="text-sm font-medium">Email / ईमेल</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    name="email" type="email" required
                    value={formData.email} onChange={handleChange}
                    placeholder="your@email.com"
                    className="pl-10"
                    data-testid="ps-login-email"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Password / पासवर्ड</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    name="password" type="password" required
                    value={formData.password} onChange={handleChange}
                    placeholder="Enter password"
                    className="pl-10"
                    data-testid="ps-login-password"
                  />
                </div>
                <div className="text-right mt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setForgotEmail(formData.email);
                      setShowForgot(true);
                      setForgotStep('request');
                    }}
                    className="text-xs text-orange-600 hover:underline"
                    data-testid="ps-forgot-password-link"
                  >
                    Forgot Password? / पासवर्ड भूल गए?
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="ps-login-submit">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Logging in...</> : 'Login / लॉगिन'}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-3">
              <p className="text-sm text-gray-500">
                Don't have an account?{' '}
                <Link to="/parichay-sangam/register" className="text-orange-600 font-medium hover:underline" data-testid="ps-register-link">
                  <UserPlus className="w-3.5 h-3.5 inline mr-1" />Register here
                </Link>
              </p>

              {/* Cross-login links — let already-registered Family / Professional users sign in
                  through their own portal and come back to the destination they were trying to reach. */}
              <div className="pt-3 border-t border-gray-100 space-y-2">
                <p className="text-xs text-gray-400 uppercase tracking-wider">Already a member elsewhere?</p>
                <div className="flex flex-col gap-1.5">
                  <Link
                    to={`/auth${nextQuery}${nextQuery ? '&' : '?'}tab=family-login`}
                    className="text-sm text-gray-600 hover:text-orange-700 inline-flex items-center justify-center gap-1.5 transition-colors"
                    data-testid="ps-family-login-link"
                  >
                    <Users className="w-3.5 h-3.5" /> Family Login →
                  </Link>
                  <Link
                    to={`/professional/login${nextQuery}`}
                    className="text-sm text-gray-600 hover:text-orange-700 inline-flex items-center justify-center gap-1.5 transition-colors"
                    data-testid="ps-professional-login-link"
                  >
                    <Briefcase className="w-3.5 h-3.5" /> Professional Login →
                  </Link>
                </div>
              </div>

              <Link to="/matrimonial" className="text-sm text-gray-400 hover:text-gray-600 flex items-center justify-center gap-1">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to Matrimonial
              </Link>
            </div>
          </>
        )}

        {showForgot && forgotStep === 'request' && (
          <form onSubmit={handleForgot} className="space-y-5" data-testid="ps-forgot-request-form">
            <div className="text-sm text-gray-600">
              Enter your registered email and we'll send you a password reset link.
              <br />
              <span className="text-xs text-gray-400">अपना पंजीकृत ईमेल दर्ज करें।</span>
            </div>
            <div>
              <Label className="text-sm font-medium">Email / ईमेल</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="email" required
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="pl-10"
                  data-testid="ps-forgot-email"
                />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="ps-forgot-submit">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</> : <><KeyRound className="w-4 h-4 mr-2" /> Send Reset Link</>}
            </Button>
            <button
              type="button"
              onClick={backToLogin}
              className="w-full text-sm text-gray-500 hover:text-orange-600 flex items-center justify-center gap-1"
              data-testid="ps-forgot-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Login
            </button>
          </form>
        )}

        {showForgot && forgotStep === 'reset' && (
          <form onSubmit={handleReset} className="space-y-5" data-testid="ps-reset-form">
            <div className="text-sm text-gray-600">
              Choose a new password for your Parichay Sangam account.
            </div>
            <div>
              <Label className="text-sm font-medium">New Password / नया पासवर्ड</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="password" required minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="pl-10"
                  data-testid="ps-reset-new-password"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Confirm Password / पुनः दर्ज करें</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="password" required minLength={6}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter the new password"
                  className="pl-10"
                  data-testid="ps-reset-confirm-password"
                />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="ps-reset-submit">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Updating...</> : 'Update Password'}
            </Button>
            <button
              type="button"
              onClick={backToLogin}
              className="w-full text-sm text-gray-500 hover:text-orange-600 flex items-center justify-center gap-1"
              data-testid="ps-reset-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Login
            </button>
          </form>
        )}

        {showForgot && forgotStep === 'success' && (
          <div className="space-y-5 text-center" data-testid="ps-forgot-success">
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto" />
            <div>
              <p className="text-gray-700 font-medium">All set!</p>
              <p className="text-sm text-gray-500 mt-1">
                {resetToken
                  ? 'Your password has been updated. Please log in with your new password.'
                  : 'If your email is registered, a reset link has been sent. Please check your inbox (and spam folder).'}
              </p>
            </div>
            <Button onClick={backToLogin} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="ps-forgot-go-login">
              Go to Login
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PSLoginPage;
